import { api } from "./client";

// Схема на заведението — региони (Зала, Тераса…) с маси и обекти.
// BE: app/features/restaurant_schema; договорът: docs/restaurant-schema.md.

export type RegionShape = "square" | "rectangle" | "wide" | "long";
export type ElementKind =
  | "table"
  | "bar"
  | "wall"
  | "plant"
  | "entrance"
  | "stage"
  | "dj";
export type ObjectKind = Exclude<ElementKind, "table">;
export type TableShape = "round" | "square" | "rectangle";
export type ElementSize = "small" | "medium" | "large" | "xl";

export interface SchemaElement {
  id: number;
  regionId: number;
  kind: ElementKind;
  // Само за маса: името Е името на масата в Маркиране (`bills.name`).
  name: string | null;
  tableShape: TableShape | null;
  seats: number | null;
  size: ElementSize;
  rotation: number;
  // Само стена: дължината в см (разпъната с дръжките); null — по `size`
  // (стена отпреди полето). На другите елементи — винаги null.
  length: number | null;
  // Центърът на елемента в логическите единици на региона (geometry.ts).
  x: number;
  y: number;
}

export interface SchemaRegion {
  id: number;
  name: string;
  // Логическият размер на региона (geometry.ts). Не се избира: новите региони
  // са `rectangle`, останалите форми са на заварени региони.
  shape: RegionShape;
  sortIndex: number;
  elements: SchemaElement[];
}

export interface RegionPayload {
  name: string;
}

export interface ElementCreatePayload {
  regionId: number;
  kind: ElementKind;
  name?: string;
  tableShape?: TableShape;
  seats?: number;
  size: ElementSize;
  rotation: number;
  // Само стена (WALL_LENGTH_MIN–MAX); на друг елемент BE го пренебрегва.
  length?: number;
  x: number;
  y: number;
}

export type ElementUpdatePayload = Partial<
  Omit<ElementCreatePayload, "kind">
>;

// GET /restaurant-schema — всички региони с елементите им (за всеки влязъл).
export const getRestaurantSchema = async (): Promise<SchemaRegion[]> => {
  const response = await api.get<SchemaRegion[]>("/restaurant-schema");
  return response.data;
};

export const createRegion = async (data: RegionPayload): Promise<SchemaRegion> => {
  const response = await api.post<SchemaRegion>(
    "/restaurant-schema/regions",
    data,
  );
  return response.data;
};

export const updateRegion = async (
  id: number,
  data: RegionPayload,
): Promise<SchemaRegion> => {
  const response = await api.put<SchemaRegion>(
    `/restaurant-schema/regions/${id}`,
    data,
  );
  return response.data;
};

// 409, ако регионът има маси/обекти — съобщението носи броя им.
export const deleteRegion = async (id: number): Promise<void> => {
  await api.delete(`/restaurant-schema/regions/${id}`);
};

export const createElement = async (
  data: ElementCreatePayload,
): Promise<SchemaElement> => {
  const response = await api.post<SchemaElement>(
    "/restaurant-schema/elements",
    data,
  );
  return response.data;
};

export const updateElement = async (
  id: number,
  data: ElementUpdatePayload,
): Promise<SchemaElement> => {
  const response = await api.put<SchemaElement>(
    `/restaurant-schema/elements/${id}`,
    data,
  );
  return response.data;
};

export const deleteElement = async (id: number): Promise<void> => {
  await api.delete(`/restaurant-schema/elements/${id}`);
};

// ── Конфигурация на маси (право manage_tables) ─────────────────────────────
// Само маси: номер, места, регион и позиция. Формата (квадратна), размерът
// (малка) и завъртането (0) ги слага BE; позицията я смята FE (autoLayout.ts).

export interface TableCreatePayload {
  name: string;
  seats: number;
  regionId: number;
  x: number;
  y: number;
}

// С `regionId` (местене в друг регион) `x` и `y` са задължителни.
export type TableUpdatePayload =
  | { name?: string; seats?: number; regionId?: undefined; x?: number; y?: number }
  | { name?: string; seats?: number; regionId: number; x: number; y: number };

// 404 непознат регион, 409 зает номер, 422 „*" в края.
export const createTable = async (data: TableCreatePayload): Promise<SchemaElement> => {
  const response = await api.post<SchemaElement>("/restaurant-schema/tables", data);
  return response.data;
};

// 404 — елементът не е маса; 409 — масата е в употреба (при преименуване) или номерът е зает.
export const updateTable = async (
  id: number,
  data: TableUpdatePayload,
): Promise<SchemaElement> => {
  const response = await api.put<SchemaElement>(`/restaurant-schema/tables/${id}`, data);
  return response.data;
};

// 409 — масата има резервация или отворена сметка.
export const deleteTable = async (id: number): Promise<void> => {
  await api.delete(`/restaurant-schema/tables/${id}`);
};

// Регион от страницата за маси — същото тяло като createRegion.
export const createTablesRegion = async (data: RegionPayload): Promise<SchemaRegion> => {
  const response = await api.post<SchemaRegion>("/restaurant-schema/tables/regions", data);
  return response.data;
};
